import JsBarcode from 'jsbarcode';

export const TRENDYOL_LOGO_SRC = '/assets/Trendyol_mp_logo.png';

export const CARGO_LOGO_MATCHERS: Array<{ pattern: RegExp; src: string }> = [
  { pattern: /trendyol\s*express|trexpress/i, src: '/assets/logo_trExpress.png' },
  { pattern: /kolay\s*gelsin|kolaygelsin/i, src: '/assets/logo_kolayGelsin.png' },
  { pattern: /yurt\s*i[çc]i|yurtici/i, src: '/assets/logo_yurtici.png' },
  { pattern: /aras/i, src: '/assets/logo_aras.png' },
  { pattern: /ptt/i, src: '/assets/logo_ptt.png' },
  { pattern: /s[üu]rat|surat/i, src: '/assets/logo_sürat.png' },
  { pattern: /hepsi\s*jet|hepsijet/i, src: '/assets/logo_hepsijet.png' },
  { pattern: /\bups\b/i, src: '/assets/logo_ups.png' },
  { pattern: /\bdhl\b/i, src: '/assets/logo_dhl.png' },
];

export const resolveCargoLogoSrc = (cargoCompany: string): string | null => {
  const normalized = cargoCompany.trim();
  if (!normalized) return null;
  return CARGO_LOGO_MATCHERS.find(({ pattern }) => pattern.test(normalized))?.src ?? null;
};

export const parseJsonObject = (raw: unknown): Record<string, any> => {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return raw && typeof raw === 'object' ? (raw as Record<string, any>) : {};
};

export const normalizeAddress = (raw: any) => {
  if (!raw || typeof raw !== 'object') return null;
  const pick = (...keys: string[]): string => {
    for (const key of keys) {
      const value = raw[key];
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return '';
  };
  const firstLast = `${pick('firstName')} ${pick('lastName')}`.trim();
  return {
    name: pick('fullName') || firstLast,
    address: pick('address', 'fullAddress', 'address1', 'addressLine'),
    city: pick('city', 'cityName'),
    district: pick('district', 'town', 'townName', 'county'),
    neighborhood: pick('neighborhood', 'districtName'),
  };
};

export const buildAddressFingerprint = (address: ReturnType<typeof normalizeAddress>) => {
  if (!address) return '';
  return [address.name, address.address, address.district, address.city, address.neighborhood]
    .join('|')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
};

export const toTurkishUpper = (value: string) => value.toLocaleUpperCase('tr-TR');

export const formatCargoCompanyLabel = (cargoCompany: string): string => {
  const normalized = cargoCompany.trim();
  if (!normalized) return '';
  if (/trendyol\s*express|trexpress/i.test(normalized)) return 'TRENDYOL EXPRESS';
  if (/kolay\s*gelsin|kolaygelsin/i.test(normalized)) return 'KOLAY GELSİN';
  if (/yurt\s*i[çc]i|yurtici/i.test(normalized)) return 'YURTİÇİ KARGO';
  if (/aras/i.test(normalized)) return 'ARAS KARGO';
  if (/ptt/i.test(normalized)) return 'PTT KARGO';
  if (/s[üu]rat|surat/i.test(normalized)) return 'SÜRAT KARGO';
  if (/hepsi\s*jet|hepsijet/i.test(normalized)) return 'HEPSİJET';
  if (/\bups\b/i.test(normalized)) return 'UPS';
  if (/\bdhl\b/i.test(normalized)) return 'DHL';
  return toTurkishUpper(normalized.replace(/\s*marketplace\s*$/i, '').trim());
};

export const generateBarcodeDataUrl = (
  text: string,
  options?: { width?: number; height?: number; barWidth?: number }
) => {
  const canvas = document.createElement('canvas');
  try {
    JsBarcode(canvas, text, {
      format: 'CODE128',
      width: options?.barWidth ?? 2,
      height: options?.height ?? 90,
      displayValue: false,
      margin: options?.width ? 4 : 8,
    });
    return canvas.toDataURL();
  } catch (error) {
    console.error('❌ [TRENDYOL PRINT] Barkod üretilemedi:', error);
    return '';
  }
};

export const resolveCargoBarcodeValue = (order: any, parsedCargoInfo: Record<string, any>) => {
  if (!order) return '';
  return (
    parsedCargoInfo?.cargoBarcode ||
    parsedCargoInfo?.cargoSenderNumber ||
    parsedCargoInfo?.cargoTrackingNumber ||
    order.trackingNumber ||
    order.shipmentPackageId ||
    order.trendyolOrderNumber ||
    order.orderNumber ||
    order.id ||
    ''
  )
    .toString()
    .trim();
};

export const parseOrderAddress = (rawAddress: unknown) => {
  if (!rawAddress) return {};
  if (typeof rawAddress === 'string') {
    try {
      return JSON.parse(rawAddress);
    } catch {
      return { address: rawAddress };
    }
  }
  return rawAddress && typeof rawAddress === 'object' ? rawAddress : {};
};

export const parseOrderCargoInfo = (rawCargoInfo: unknown) => {
  if (!rawCargoInfo) return {};
  if (typeof rawCargoInfo === 'string') {
    try {
      return JSON.parse(rawCargoInfo);
    } catch {
      return {};
    }
  }
  return rawCargoInfo && typeof rawCargoInfo === 'object' ? rawCargoInfo : {};
};
